import * as React from 'react';
import ApiClient from '../../Services/ApiClient';
import { validateText } from '../../Utils/validation';
import AuthContext from '../../store/authContext';
import { useAlert } from '../ui/alertProvider';
import { useResources, useComponentResources } from '../../store/resourcesProvider';
import { Resource } from '../../../Models/Resource';
import AboutImagesAdmin from './aboutImagesAdmin';
import FocusTrap from '../ui/focusTrap';
import ConfirmModal from '../ui/confirmModal';

type Status = { type: 'success' | 'error'; text: string } | null;

const ResourcesAdmin: React.FC = () => {
    const { isAdmin, isSuperAdmin } = React.useContext(AuthContext);
    const [resources, setResources] = React.useState<Resource[]>([]);
    const [editing, setEditing] = React.useState<Resource | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [status, setStatus] = React.useState<Status>(null);
    // Collapsible sections
    const [showAboutImages, setShowAboutImages] = React.useState(false);
    const [showResources, setShowResources] = React.useState(true);
    // Mail templates
    const [showMailTemplates, setShowMailTemplates] = React.useState(false);
    const [mailTemplates, setMailTemplates] = React.useState<any[]>([]);
    const [editingTemplate, setEditingTemplate] = React.useState<any | null>(null);
    const [mailLoading, setMailLoading] = React.useState(false);
    const alert = useAlert();
    const resourcesCtx = useResources();
    // preload and read component-scoped resources for Resources admin (includes userId automatically)
    const pageRes = useComponentResources('Resources', [
        'Resources.AdminTitle','Resources.AddNew','Resources.Refresh','Resources.ExportJson','Resources.ImportedDefaults','Resources.ImportJsonLabel',
        'Resources.Col.Id','Resources.Col.Key','Resources.Col.Value','Resources.Col.Culture','Resources.Col.Actions',
        'Resources.NoPermission',
        // action labels & modal
        'Resources.Edit','Resources.Delete','Resources.DeleteConfirm','Resources.Save','Resources.Saving','Resources.Cancel','Resources.SavedSuccess',
        // upload labels
        'Resources.Upload','Resources.Uploading'
    ]);

    React.useEffect(() => {
        if (!isAdmin && !isSuperAdmin) return;
        load();
    }, [isAdmin, isSuperAdmin]);

    const load = async () => {
        try {
            const data: any = await ApiClient.get('/resources');
            setResources(data || []);
            setStatus(null);
        } catch (err) {
            console.error('Failed to load resources', err);
            alert.showAlert(resourcesCtx.get('Resources.LoadFailed', 'Failed to load resources: ') + ((err as any)?.message || String(err)), 'error');
        }
    };

    const exportJson = async () => {
        try {
            const data: any = await ApiClient.get('/resources');
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'resources.json';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) { console.error('Export failed', err); alert.showAlert(resourcesCtx.get('Resources.ExportFailed','Export failed: ') + ((err as any)?.message || String(err)), 'error'); }
    };

    const importJson = async (file: File | null) => {
        if (!file) return;
        try {
            const txt = await file.text();
            const parsed = JSON.parse(txt);
            if (!Array.isArray(parsed)) throw new Error('Invalid JSON format - expected array');
            const res = await ApiClient.post('/resources/import', parsed);
            const count = res?.processed ?? 0;
            setStatus({ type: 'success', text: pageRes['Resources.SavedSuccess'] ?? resourcesCtx.get('Resources.SavedSuccess', `Imported ${count} resources successfully.`) });
            await load();
        } catch (err) { console.error('Import failed', err); alert.showAlert(resourcesCtx.get('Resources.ImportFailed','Import failed: ') + ((err as any)?.message || String(err)), 'error'); }
    };

    const startNew = () => setEditing({ id: 0, key: '', value: '', culture: null });

    const save = async () => {
        if (!editing) return;
        setLoading(true);
            try {
            // client-side validation
            const keyErr = validateText(editing.key || '', 1, 200);
            if (keyErr) { alert.showAlert(keyErr, 'error'); setLoading(false); return; }
            if (editing.id && editing.id > 0) {
                await ApiClient.put(`/resources/${editing.id}`, editing);
            } else {
                await ApiClient.post('/resources', editing);
            }
            await load();
            // animate modal close
            try { setModalClosing(true); } catch {}
            setTimeout(() => { setEditing(null); setModalClosing(false); }, 200);
            alert.showAlert(pageRes['Resources.SavedSuccess'] ?? resourcesCtx.get('Resources.SavedSuccess','Resource saved successfully.'), 'success');
        } catch (err) {
            console.error('Save failed', err);
            alert.showAlert(resourcesCtx.get('Resources.SaveFailed','Save failed: ') + ((err as any)?.message || String(err)), 'error');
        } finally { setLoading(false); }
    };

    // show confirm modal before deleting
    const [confirmDeleteId, setConfirmDeleteId] = React.useState<number | null>(null);
    const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
    // modal animation state for edit modal
    const [modalClosing, setModalClosing] = React.useState<boolean>(false);

    const remove = async (id: number) => {
        // open confirm modal
        setConfirmDeleteId(id);
    };

    const performDelete = async (id: number | null) => {
        if (!id) return;
        setConfirmLoading(true);
        try {
            await ApiClient.delete(`/resources/${id}`);
            await load();
            // show inline status toast instead of global alert
            setStatus({ type: 'success', text: pageRes['Resources.Deleted'] ?? resourcesCtx.get('Resources.Deleted','Resource deleted.') });
            // auto-hide status after 4s
            try { setTimeout(() => setStatus(null), 4000); } catch {}
        } catch (err) {
            console.error('Delete failed', err);
            alert.showAlert(resourcesCtx.get('Resources.DeleteFailed','Delete failed: ') + ((err as any)?.message || String(err)), 'error');
        } finally {
            setConfirmLoading(false);
            setConfirmDeleteId(null);
        }
    };

    const loadMailTemplates = async () => {
        setMailLoading(true);
        try {
            // You may want to get the current siteId dynamically if needed
            const res = await ApiClient.get('/api/mailtemplates/site/1');
            setMailTemplates(res || []);
        } catch (e) {
            setMailTemplates([]);
        }
        setMailLoading(false);
    };

    if (!isAdmin && !isSuperAdmin) return <div>{pageRes['Resources.NoPermission'] ?? resourcesCtx.get('Resources.NoPermission','אין הרשאה')}</div>;

    return (
        <div className="items-admin">
            <h2 className="items-title">{pageRes['Resources.AdminTitle'] ?? resourcesCtx.get('Resources.AdminTitle','ניהול תוכן')}</h2>

            {/* Collapsible toggles */}
            <div className="flex gap-8 mb-12">
                <button className="btn" onClick={() => setShowResources(v => !v)}>{showResources ? 'הסתר ניהול משאבים' : 'הצג ניהול משאבים'}</button>
                <button className="btn" onClick={() => setShowAboutImages(v => !v)}>{showAboutImages ? 'הסתר תמונות אודות' : 'הצג תמונות אודות'}</button>
                <button className="btn" onClick={() => { setShowMailTemplates(v => !v); if (!showMailTemplates) loadMailTemplates(); }}>{showMailTemplates ? 'הסתר תבניות מייל' : 'הצג תבניות מייל'}</button>
            </div>

            {/* Resources Section */}
            {showResources && (
                <>
                <div className="flex gap-8 mb-12">
                    <button className="btn btn-primary" onClick={startNew}>{pageRes['Resources.AddNew'] ?? resourcesCtx.get('Resources.AddNew','הוסף תוכן חדש')}</button>
                    <button className="btn" onClick={load}>{pageRes['Resources.Refresh'] ?? resourcesCtx.get('Resources.Refresh','רענן')}</button>
                    <button className="btn" onClick={exportJson}>{pageRes['Resources.ExportJson'] ?? resourcesCtx.get('Resources.ExportJson','ייצא JSON')}</button>
                    <button className="btn" onClick={async () => {
                        try {
                            const res: any = await ApiClient.post('/resources/import-file');
                            alert.showAlert((pageRes['Resources.ImportedDefaults'] ?? resourcesCtx.get('Resources.ImportedDefaults','Imported defaults: ')) + (res?.processed ?? 0), 'success');
                            await load();
                        } catch (err) { console.error('Import defaults failed', err); alert.showAlert(resourcesCtx.get('Resources.ImportDefaultsFailed','Import defaults failed'), 'error'); }
                    }}>{pageRes['Resources.DownloadSeed'] ?? resourcesCtx.get('Resources.DownloadSeed','הורד קובץ דוגמא')}</button>
                   
                    </div>
                    <div className="flex gap-8 mb-12">
                        <label className="file-label btn">
                            {pageRes['Resources.ImportJsonLabel'] ?? resourcesCtx.get('Resources.ImportJsonLabel', 'ייבא JSON')}
                            <input type="file" accept="application/json" style={{ display: 'none' }} onChange={e => importJson(e.target.files ? e.target.files[0] : null)} />
                        </label>
                    </div>
                {status && (
                    <div className="mb-12">
                        <div className={`status-box ${status.type === 'error' ? 'status-error' : 'status-success'}`}>
                            {status.text}
                        </div>
                    </div>
                )}
            {/* Confirm delete modal */}
            <ConfirmModal
                open={confirmDeleteId != null}
                title={pageRes['Resources.Delete'] ?? resourcesCtx.get('Resources.Delete','מחק')}
                message={pageRes['Resources.DeleteConfirm'] ?? resourcesCtx.get('Resources.DeleteConfirm','האם למחוק?')}
                loading={confirmLoading}
                onConfirm={() => performDelete(confirmDeleteId)}
                onCancel={() => setConfirmDeleteId(null)}
            />
                <div className="table-container">
                    <table className="items-table">
                        <thead>
                        <tr>
                            <th>{pageRes['Resources.Col.Id'] ?? resourcesCtx.get('Resources.Col.Id','מזהה')}</th>
                            <th>{pageRes['Resources.Col.Key'] ?? resourcesCtx.get('Resources.Col.Key','מפתח')}</th>
                            <th>{pageRes['Resources.Col.Value'] ?? resourcesCtx.get('Resources.Col.Value','ערך')}</th>
                            <th>{pageRes['Resources.Col.Culture'] ?? resourcesCtx.get('Resources.Col.Culture','ע\"ש')}</th>
                            <th>{pageRes['Resources.Col.Actions'] ?? resourcesCtx.get('Resources.Col.Actions','פעולות')}</th>
                        </tr>
                        </thead>
                        <tbody>
                            {resources.map(r => (
                                <tr key={r.id}>
                                    <td>{r.id}</td>
                                    <td>{r.key}</td>
                                    <td className="nowrap-ellipsis" style={{maxWidth:400}}>{r.value}</td>
                                    <td>{r.culture}</td>
                                    <td>
                                        <button className="btn" onClick={() => setEditing(r)}>{pageRes['Resources.Edit'] ?? resourcesCtx.get('Resources.Edit','ערוך')}</button>
                                        <button className="btn btn-danger" onClick={() => remove(r.id)}>{pageRes['Resources.Delete'] ?? resourcesCtx.get('Resources.Delete','מחק')}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

            {/* Mobile-friendly cards (hide table via CSS on small screens) */}
            <div className="cards-list">
                {resources.map(r => (
                    <div key={r.id} className="item-card">
                        <div className="item-row-flex" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="resource-main">
                                <div className="resource-key" title={r.key} style={{ fontWeight: 700 }}>{r.key}</div>
                                <div className="resource-value" title={r.value} style={{ color: '#666', maxWidth: 240 }}>{r.value}</div>
                                <div style={{ color: '#999', fontSize: 12 }}>{r.culture}</div>
                            </div>
                            <div className="item-actions" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <button className="btn" onClick={() => setEditing(r)}>Edit</button>
                                <button className="btn btn-danger" onClick={() => remove(r.id)}>Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
                </>
            )}

            {/* About Images Section */}
            {showAboutImages && <AboutImagesAdmin />}

            {/* Mail Templates Section */}
            {showMailTemplates && (
                <div style={{marginTop:24, marginBottom:24, border:'1px solid #ccc', borderRadius:8, padding:16}}>
                    <h3>Mail Templates</h3>
                    {mailLoading ? <div>Loading...</div> : (
                        <>
                        {mailTemplates.length === 0 && <div>No mail templates found.</div>}
                        {mailTemplates.map(tmpl => (
                            <div key={tmpl.id} style={{marginBottom:16, borderBottom:'1px solid #eee', paddingBottom:8}}>
                                <div><strong>Key:</strong> {tmpl.templateKey}</div>
                                <div><strong>Description:</strong> {tmpl.description || ''}</div>
                                <button className="btn" onClick={() => setEditingTemplate(tmpl)}>Edit</button>
                            </div>
                        ))}
                        </>
                    )}
                    {/* Edit modal for mail template */}
                    {editingTemplate && (
                        <div className="modal-overlay" role="dialog" aria-modal="true">
                            <FocusTrap onEscape={() => setEditingTemplate(null)}>
                                <div className="modal-content modal-large">
                                    <button className="modal-close" onClick={() => setEditingTemplate(null)}>&times;</button>
                                    <h3>Edit Mail Template: {editingTemplate.templateKey}</h3>
                                    <div className="form-group">
                                        <label className="form-label">Description</label>
                                        <input className="form-input" value={editingTemplate.description || ''} onChange={e => setEditingTemplate({...editingTemplate, description: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">HTML Content</label>
                                        <textarea className="form-textarea textarea-tall" value={editingTemplate.htmlContent} onChange={e => setEditingTemplate({...editingTemplate, htmlContent: e.target.value})} />
                                    </div>
                                    <div style={{display:'flex', gap:8}}>
                                        <button className="btn btn-primary" onClick={async () => {
                                            setMailLoading(true);
                                            await ApiClient.post('/api/mailtemplates', editingTemplate);
                                            setEditingTemplate(null);
                                            await loadMailTemplates();
                                            setMailLoading(false);
                                        }}>Save</button>
                                        <button className="btn" onClick={() => setEditingTemplate(null)}>Cancel</button>
                                    </div>
                                    <div style={{marginTop:12, fontSize:12, color:'#888'}}>
                                        <div>Available placeholders: {'{PatientName}'}, {'{PatientEmail}'}, {'{UserName}'}, etc.</div>
                                    </div>
                                </div>
                            </FocusTrap>
                        </div>
                    )}
                </div>
            )}
            {status && (
                <div className="mb-12">
                    <div className={`status-box ${status.type === 'error' ? 'status-error' : 'status-success'}`}>
                        {status.text}
                    </div>
                </div>
            )}
            <div className="table-container">
                <table className="items-table">
                    <thead>
                    <tr>
                        <th>{pageRes['Resources.Col.Id'] ?? resourcesCtx.get('Resources.Col.Id','מזהה')}</th>
                        <th>{pageRes['Resources.Col.Key'] ?? resourcesCtx.get('Resources.Col.Key','מפתח')}</th>
                        <th>{pageRes['Resources.Col.Value'] ?? resourcesCtx.get('Resources.Col.Value','ערך')}</th>
                        <th>{pageRes['Resources.Col.Culture'] ?? resourcesCtx.get('Resources.Col.Culture','ע\"ש')}</th>
                        <th>{pageRes['Resources.Col.Actions'] ?? resourcesCtx.get('Resources.Col.Actions','פעולות')}</th>
                    </tr>
                    </thead>
                    <tbody>
                        {resources.map(r => (
                            <tr key={r.id}>
                                <td>{r.id}</td>
                                <td>{r.key}</td>
                                <td style={{maxWidth:400}}>{r.value}</td>
                                <td>{r.culture}</td>
                                <td>
                                    <button className="btn" onClick={() => setEditing(r)}>{pageRes['Resources.Edit'] ?? resourcesCtx.get('Resources.Edit','ערוך')}</button>
                                    <button className="btn btn-danger" onClick={() => remove(r.id)}>{pageRes['Resources.Delete'] ?? resourcesCtx.get('Resources.Delete','מחק')}</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>


            {editing && (
                <div className="modal-overlay" role="dialog" aria-modal="true">
                    <FocusTrap onEscape={() => setEditing(null)}>
                        <div className={`modal-content modal-large ${modalClosing ? 'modal-exit' : 'modal-enter'}`}>
                            <button className="modal-close" onClick={() => setEditing(null)}>&times;</button>
                            <h3 className="modal-title">{editing.id && editing.id > 0 ? (pageRes['Resources.Edit'] ?? resourcesCtx.get('Resources.Edit','ערוך')) : (pageRes['Resources.AddNew'] ?? resourcesCtx.get('Resources.AddNew','חדש'))}</h3>
                            <div className="form-group">
                                <label className="form-label">{pageRes['Resources.Col.Key'] ?? resourcesCtx.get('Resources.Col.Key','מפתח')}</label>
                                <input className="form-input" value={editing.key} onChange={e => setEditing({...editing, key: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['Resources.Col.Value'] ?? resourcesCtx.get('Resources.Col.Value','ערך')}</label>
                                <textarea className="form-textarea" value={editing.value} onChange={e => setEditing({...editing, value: e.target.value})} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{pageRes['Resources.Col.Culture'] ?? resourcesCtx.get('Resources.Col.Culture','תרבות (אופציונלי)')}</label>
                                <input className="form-input" value={editing.culture || ''} onChange={e => setEditing({...editing, culture: e.target.value || null})} />
                            </div>
                            <div className="flex gap-8">
                                <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? (pageRes['Resources.Saving'] ?? resourcesCtx.get('Resources.Saving','שומר...')) : (pageRes['Resources.Save'] ?? resourcesCtx.get('Resources.Save','שמור'))}</button>
                                <button className="btn" onClick={() => setEditing(null)}>{pageRes['Resources.Cancel'] ?? resourcesCtx.get('Resources.Cancel','בטל')}</button>
                            </div>
                        </div>
                    </FocusTrap>
                </div>
            )}
        </div>
    );
};

export default ResourcesAdmin;
